'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useI18n } from '@/lib/i18n'
import { gameQueries } from '@/lib/queries'
import { RefreshCw } from 'lucide-react'
import { useState } from 'react'

export function ControlPanel() {
  const { t } = useI18n()
  const [isRefreshingModels, setIsRefreshingModels] = useState(false)
  const [isLoadingModels, setIsLoadingModels] = useState(false)
  const [responsiveLowCost, setResponsiveLowCost] = useState<string[]>([])
  const [responsiveHighPerf, setResponsiveHighPerf] = useState<string[]>([])
  const [poolModels, setPoolModels] = useState<string[]>([])

  const handleRefreshResponsiveModels = async () => {
    setIsRefreshingModels(true)

    try {
      await gameQueries.refreshResponsiveModelsCache()
      // Could show a success toast here
    }
    catch (error) {
      console.error('Failed to refresh responsive models cache:', error)
      // Could show an error toast here
    }
    finally {
      setIsRefreshingModels(false)
    }
  }

  const handleShowResponsiveModels = async () => {
    setIsLoadingModels(true)

    try {
      const [responsiveResult, poolsResult] = await Promise.all([
        gameQueries.getResponsiveModels(),
        gameQueries.getGameModelPools(),
      ])
      setResponsiveLowCost(responsiveResult.data.lowCost || [])
      setResponsiveHighPerf(responsiveResult.data.highPerformance || [])

      if (poolsResult.success && poolsResult.data) {
        const low = Object.values(poolsResult.data.lowCost ?? {}).flat()
        const high = Object.values(poolsResult.data.highPerformance ?? {}).flat()
        setPoolModels([...new Set([...low, ...high])])
      }
    }
    catch (error) {
      console.error('Failed to get responsive models or pools:', error)
      // Could show an error toast here
    }
    finally {
      setIsLoadingModels(false)
    }
  }

  // All models from configured pools (for slow vs responsive comparison)
  const allModels = poolModels.length > 0 ? poolModels : []
  const allResponsive = [...responsiveLowCost, ...responsiveHighPerf]

  const slowModels = allResponsive.length > 0 && allModels.length > 0
    ? allModels.filter(model => !allResponsive.includes(model))
    : []

  return (
    <div className="bg-slate-50 px-4 py-8 dark:bg-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-foreground md:text-2xl lg:text-3xl">
            {t('admin.controlPanel.title', 'Control Panel')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('admin.controlPanel.description', 'System administration and cache management tools')}
          </p>
        </div>

        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>
                {t('admin.controlPanel.gameModels', 'Game Models')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <div className="flex gap-4">
                  <Button
                    onClick={handleRefreshResponsiveModels}
                    disabled={isRefreshingModels}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    {isRefreshingModels ? (
                      <>
                        <RefreshCw className="size-4 animate-spin" />
                        Calculating...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="size-4" />
                        Calculate Responsive Model
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleShowResponsiveModels}
                    disabled={isLoadingModels}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    {isLoadingModels ? (
                      <>
                        <RefreshCw className="size-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="size-4" />
                        Show Responsive Model
                      </>
                    )}
                  </Button>
                </div>

                {(responsiveLowCost.length > 0 || responsiveHighPerf.length > 0) && (
                  <>
                    <div className="mt-4 grid gap-6 md:grid-cols-2">
                      {/* Low Cost Models */}
                      <div>
                        <h4 className="mb-2 text-sm font-medium text-green-600 dark:text-green-400">
                          Low Cost Responsive ({responsiveLowCost.length})
                        </h4>
                        <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-800">
                          {responsiveLowCost.length > 0
                            ? (
                                <div className="grid grid-cols-1 gap-2">
                                  {responsiveLowCost.map(model => (
                                    <div key={model} className="truncate rounded border border-green-200 bg-white px-2 py-1 font-mono text-xs dark:border-green-900 dark:bg-slate-700">
                                      {model}
                                    </div>
                                  ))}
                                </div>
                              )
                            : (
                                <div className="text-xs text-muted-foreground">No models available</div>
                              )}
                        </div>
                      </div>

                      {/* High Performance Models */}
                      <div>
                        <h4 className="mb-2 text-sm font-medium text-blue-600 dark:text-blue-400">
                          High Performance Responsive ({responsiveHighPerf.length})
                        </h4>
                        <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-800">
                          {responsiveHighPerf.length > 0
                            ? (
                                <div className="grid grid-cols-1 gap-2">
                                  {responsiveHighPerf.map(model => (
                                    <div key={model} className="truncate rounded border border-blue-200 bg-white px-2 py-1 font-mono text-xs dark:border-blue-900 dark:bg-slate-700">
                                      {model}
                                    </div>
                                  ))}
                                </div>
                              )
                            : (
                                <div className="text-xs text-muted-foreground">No models available</div>
                              )}
                        </div>
                      </div>
                    </div>

                    {slowModels.length > 0 && (
                      <div className="mt-4">
                        <h4 className="mb-2 text-sm font-medium text-red-600 dark:text-red-400">
                          Slow/Unresponsive Models ({slowModels.length}):
                        </h4>
                        <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-800">
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                            {slowModels.map(model => (
                              <div key={model} className="truncate rounded border border-red-200 bg-white px-2 py-1 font-mono text-sm dark:border-red-900 dark:bg-slate-700">
                                {model}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
